import React, { useEffect, useState } from "react";
import {
    Card,
    CardContent,
    CardHeader,
    Typography,
    TextField,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Chip,
    Alert,
    AlertTitle,
    Box,
    CircularProgress,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";

import NotificationsIcon from "@mui/icons-material/Notifications";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import axios from "axios";
import { ApiLink } from "../../APILink/ApiLink";
import authHeader from "../../Auth/authHeader";
import ReadNotification from "./ReadNotification";
import { errorAlert, handleSuccess, infoAlert, updateAlert } from "../../Components/Alerts";
import CancelModal from "../../Components/CancelModal";

const priorityColors = {
    urgent: "error",
    high: "warning",
    medium: "info",
    low: "default",
};

export default function Notification() {
    const [formData, setFormData] = useState({
        title: "",
        message: "",
        version: "",
        visibleTo: "",
        isActive: true,
    });

    const [notificationID, setNotificationID] = useState("")
    const [isInsertForm, setIsInsertForm] = useState(true);
    const [showEditBtn, setShowEditBtn] = useState(false);
    const [showModal, setShowModal] = useState(false);

    const [openInsertForm, setOpenInsertForm] = useState(false);
    const [showCloseBtn, setShowCloseBtn] = useState(false);

    const [role, setRole] = useState("");

    const handleEdit = (notification) => {
        setFormData({
            title: notification.Title,
            message: notification.Message,
            version: notification.Version,
            visibleTo: notification.VisibleTo,
            isActive: notification.IsActive
        });
        setNotificationID(notification.NotificationID);
        setIsInsertForm(false);
        setOpenInsertForm(true);
        setShowCloseBtn(true);
    }

    const [getData, setGetData] = useState(false);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const headers = authHeader();

    const validateForm = () => {
        const newErrors = {};
        if (!formData.title.trim()) newErrors.title = "Title is required";
        if (!formData.message.trim()) newErrors.message = "Message is required";
        if (!formData.visibleTo) newErrors.visibleTo = "Visibility setting is required";
        if (formData.visibleTo === "Custom" && !formData.customCompanyId.trim())
            newErrors.customCompanyId = "Company ID is required for custom visibility";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const action = e.nativeEvent.submitter?.value;

        // Validimi i fushave të domosdoshme
        const isFormValid = () =>
            formData.title && formData.message && formData.version && formData.visibleTo;

        if (!isFormValid()) {
            errorAlert("Ju lutem plotësoni të gjitha fushat e detyrueshme.");
            return;
        }

        setIsSubmitting(true);

        try {
            let response;

            if (action === "edit") {
                // Përditësimi i njoftimit ekzistues
                const notificationData = {
                    ...formData,
                    NotificationID: notificationID,
                };

                response = await axios.put(
                    `${ApiLink.API_URL}Notification/updateNotification`,
                    notificationData,
                    { headers }
                );

                updateAlert("Njoftimi u modifikua me sukses");
            } else {
                // Krijimi i njoftimit të ri
                response = await axios.post(
                    `${ApiLink.API_URL}Notification`,
                    formData,
                    { headers }
                );

                handleSuccess("Njoftimi u krijua me sukses!");
            }

            // ✅ Logjika që duhet të ekzekutohet pas suksesit (për të dy rastet)
            setFormData({
                title: "",
                message: "",
                version: "",
                visibleTo: "",
                isActive: true,
            });

            setIsInsertForm(true);     // Nëse ke formë të veçantë për insert
            setGetData(true);          // Rifresko të dhënat (map-i)

        } catch (error) {
            console.error(error);
            errorAlert("Gabim gjatë përpunimit të njoftimit.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const openForm = () => {
        setOpenInsertForm(true);
        setShowCloseBtn(true);
    }
    const closeForm = () => {
        setOpenInsertForm(false);
        setShowCloseBtn(false);
        clearFields();
    }

    const fetchRole = async () => {
        try {
            const response = await axios.get(ApiLink.API_URL + 'AuthProva/getrole', { headers });

            if (response.data.UserRole && response.data.UserRole !== undefined) {
                setRole(response.data.UserRole);
            } else {
                errorAlert("Undefined Role.");
            }
        } catch (error) {
            console.error("Error fetching role:", error);
            // ... (pjesa tjetër e kodit)
        }
    };

    const clearFields = () => {
        setFormData({
            title: "",
            message: "",
            version: "",
            visibleTo: "",
            isActive: true
        });
        setNotificationID("");
        setIsInsertForm(true);
        setShowModal(false);
        setShowEditBtn(false);
    }

    useEffect(() => {
        fetchRole();
    }, [])
    return (
        <div style={{ paddingTop: "50px" }}>
            <CancelModal
                open={showModal}
                onClose={() => setShowModal(false)}
                title="Konfirmim"
                description="Jeni të sigurt që dëshironi ti anuloni ndryshimet e bera?"
                confirmText="po"     // Custom confirm button text
                cancelText="jo"      // Custom cancel button text
                onConfirm={() => clearFields()}
            />
            {
                role === "acctree" && (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: { xs: "column", lg: "row" },
                            alignItems: { lg: "center" },
                            justifyContent: { lg: "space-between" },
                            gap: 2, // spacing 2 = 16px
                            mb: 3, // margin bottom
                        }}
                    >
                        <Box>
                            <Typography
                                variant="h4"
                                component="h1"
                                sx={{ fontWeight: "bold", color: "text.primary" }}
                            >
                                Menaxhimi i njoftimeve
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ color: "text.secondary", mt: 0.5 }}
                            >
                                Ketu mund te insertoni, modifikoni dhe fshini njoftimet
                            </Typography>
                        </Box>

                        {
                            showCloseBtn ?
                                <Button
                                    type="button"
                                    variant="contained"
                                    onClick={closeForm}
                                >
                                    Mbyll formen e insertimit
                                </Button>
                                :
                                <Button
                                    type="button"
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={openForm}
                                >
                                    Krijo njoftim te ri
                                </Button>
                        }
                    </Box>
                )
            }

            {
                openInsertForm ?
                    <>
                        <Box mx="auto" mt={4} mb={6} border="1px solid black">
                            {/* Header */}
                            <Box textAlign="center" mb={4}>
                                <Box
                                    display="inline-flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    width={48}
                                    height={48}
                                    borderRadius="50%"
                                    bgcolor="primary.main"
                                    color="white"
                                    mb={1}
                                    mx="auto"
                                >
                                    {
                                        isInsertForm ?
                                            <AddIcon />
                                            :
                                            <EditIcon />
                                    }
                                </Box>
                                <Typography variant="h4" component="h1" gutterBottom>
                                    {
                                        isInsertForm ?
                                            "Krijo Notifikim te ri"
                                            :
                                            "Modifiko Njoftimin"
                                    }
                                </Typography>
                                <Typography color="textSecondary">
                                    {
                                        isInsertForm ?
                                            "Notifikim i ri për ti informuar përdoruesit"
                                            :
                                            "Kujdes, ju jeni duke e modifikuar njoftimin"
                                    }
                                </Typography>
                            </Box>

                            {/* Form */}
                            <Card>
                                <CardHeader
                                    avatar={<NotificationsIcon />}
                                    title={<Typography variant="h6">Të dhënat e njoftimit</Typography>}
                                />
                                <CardContent>
                                    <form onSubmit={handleSubmit} noValidate>
                                        {/* Title */}
                                        <TextField
                                            label="Title"
                                            required
                                            fullWidth
                                            margin="normal"
                                            value={formData.title}
                                            onChange={(e) => { handleInputChange("title", e.target.value); setShowEditBtn(true); }}
                                            error={Boolean(errors.title)}
                                            helperText={errors.title}
                                            autoComplete="off"
                                        />

                                        {/* Message */}
                                        <TextField
                                            label="Message"
                                            required
                                            fullWidth
                                            margin="normal"
                                            multiline
                                            value={formData.message}
                                            onChange={(e) => { handleInputChange("message", e.target.value); setShowEditBtn(true); }}
                                            error={Boolean(errors.message)}
                                            helperText={errors.message}
                                            autoComplete="off"
                                        />

                                        {/* Version and Priority */}
                                        <Box display="flex" gap={2} flexWrap="wrap" mt={2}>
                                            <TextField
                                                label="Version"
                                                fullWidth
                                                value={formData.version}
                                                onChange={(e) => { handleInputChange("version", e.target.value); setShowEditBtn(true); }}
                                                autoComplete="off"
                                            />

                                            {/* <FormControl fullWidth>
                                <InputLabel id="priority-label">Priority</InputLabel>
                                <Select
                                    labelId="priority-label"
                                    value={formData.priority}
                                    label="Priority"
                                    onChange={(e) => handleInputChange("priority", e.target.value)}
                                >
                                    {["low", "medium", "high", "urgent"].map((p) => (
                                        <MenuItem key={p} value={p}>
                                            <Chip label={p.charAt(0).toUpperCase() + p.slice(1)} color={priorityColors[p]} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl> */}
                                        </Box>

                                        {/* Visible To */}
                                        <FormControl fullWidth margin="normal" error={Boolean(errors.visibleTo)}>
                                            <InputLabel id="visibleTo-label">Visible To *</InputLabel>
                                            <Select
                                                labelId="visibleTo-label"
                                                value={formData.visibleTo}
                                                label="Visible To *"
                                                onChange={(e) => { handleInputChange("visibleTo", e.target.value); setShowEditBtn(true); }}
                                            >
                                                <MenuItem value="All">All Users</MenuItem>
                                                <MenuItem value="Admins">Admins Only</MenuItem>
                                                <MenuItem value="Staff">Staff Only</MenuItem>
                                                <MenuItem value="Custom">Custom Company ID</MenuItem>
                                            </Select>
                                            {errors.visibleTo && (
                                                <Typography color="error" variant="caption" mt={0.5}>
                                                    {errors.visibleTo}
                                                </Typography>
                                            )}
                                        </FormControl>

                                        {/* Custom Company ID */}
                                        {/* {formData.visibleTo === "Custom" && (
                                <TextField
                                    label="Company ID *"
                                    fullWidth
                                    margin="normal"
                                    value={formData.customCompanyId}
                                    onChange={(e) => handleInputChange("customCompanyId", e.target.value)}
                                    error={Boolean(errors.customCompanyId)}
                                    helperText={errors.customCompanyId}
                                />
                            )} */}

                                        {/* Preview */}
                                        {(formData.title || formData.message) && (
                                            <Alert severity="info" sx={{ mt: 3 }}>
                                                <AlertTitle>
                                                    <NotificationsIcon fontSize="small" sx={{ verticalAlign: "middle", mr: 1 }} />
                                                    Preview
                                                </AlertTitle>
                                                <Typography variant="subtitle1" fontWeight="bold">
                                                    {formData.title || "Notification Title"}
                                                </Typography>
                                                <Typography variant="body2" mb={1}>
                                                    {formData.message || "Notification message will appear here"}
                                                </Typography>
                                                <Box display="flex" gap={1} flexWrap="wrap" fontSize="0.8rem" color="text.secondary">
                                                    {formData.version && (
                                                        <Chip label={`v${formData.version}`} size="small" variant="outlined" />
                                                    )}
                                                    {/* <Chip
                                        label={formData.priority.charAt(0).toUpperCase() + formData.priority.slice(1)}
                                        size="small"
                                        variant="outlined"
                                        color={priorityColors[formData.priority]}
                                    /> */}
                                                    {formData.visibleTo && (
                                                        <Typography component="span" sx={{ ml: 1 }}>
                                                            Visible to:{" "}
                                                            {formData.visibleTo === "Custom"
                                                                ? formData.customCompanyId || "Custom"
                                                                : formData.visibleTo}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Alert>
                                        )}

                                        {/* Buttons */}
                                        {
                                            isInsertForm ?
                                                <Box display="flex" gap={2} mt={4}>
                                                    <Button
                                                        type="submit"
                                                        variant="contained"
                                                        name="action"
                                                        value="insert"
                                                        disabled={isSubmitting}
                                                        startIcon={isSubmitting ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                                                        fullWidth
                                                    >
                                                        {isSubmitting ? "duke u procesuar" : "Ruaj njoftimin"}
                                                    </Button>

                                                    <Button
                                                        variant="outlined"
                                                        fullWidth
                                                        type="button"
                                                        onClick={() => {
                                                            clearFields();
                                                        }}
                                                    >
                                                        Pastro fushat
                                                    </Button>
                                                </Box>
                                                :
                                                showEditBtn ?
                                                    <Box display="flex" gap={2} mt={4}>
                                                        <Button
                                                            type="submit"
                                                            variant="contained"
                                                            name="action"
                                                            value="edit"
                                                            disabled={isSubmitting}
                                                            startIcon={isSubmitting ? <CircularProgress size={20} /> : <EditIcon />}
                                                            fullWidth
                                                        >
                                                            {isSubmitting ? "duke u procesuar" : "Ruaj Ndryshimet"}
                                                        </Button>

                                                        <Button
                                                            variant="outlined"
                                                            fullWidth
                                                            onClick={() => {
                                                                setShowModal(true);
                                                                // Replace with your redirect logic
                                                                // alert("Cancel clicked");
                                                            }}
                                                        >
                                                            Anulo ndryshimet
                                                        </Button>
                                                    </Box>
                                                    :
                                                    <Box display="flex" gap={2} mt={4}>
                                                        <Button
                                                            type="button"
                                                            variant="outlined"
                                                            fullWidth
                                                            onClick={(e) => {
                                                                e.preventDefault(); // ky ndalon submit-in, për çdo rast
                                                                closeForm();
                                                            }}
                                                        >
                                                            Mbyll formen e editimit
                                                        </Button>

                                                    </Box>
                                        }

                                    </form>
                                </CardContent>
                            </Card>
                        </Box>
                    </>
                    :
                    null
            }

            <hr></hr>

            <ReadNotification getData={getData} onEdit={handleEdit} />
        </div>
    );
}
